import React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import InnerShadowBox from "components/Shared/InnerShadowBox";
import LoadingWrapper from "components/Shared/LoadingWrapper";

const INITIAL_VALUE = { name: "", status: "Active" };

const ListOptionForm = ({
  isBusy = false,
  option = null,
  groupOptions = [],
  valueLabel = null,
  valueType = "String",
  readOnly = false,
  onSubmit = () => {},
  onClose = () => {},
}) => {
  // react form hook
  const {
    control,
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: INITIAL_VALUE,
  });

  React.useEffect(() => {
    if (!option) return;

    const { name, group, status } = option;

    reset({
      name,
      status,
      ...(option?.value && { value: option?.value ?? 1 }),
      ...(!!groupOptions?.length && { group }),
    });

    return () => {
      reset(INITIAL_VALUE);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option]);

  return (
    <LoadingWrapper loading={isBusy}>
      <InnerShadowBox padding={3}>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <TextField
              {...register("name", {
                required: "Please provide a name.",
              })}
              fullWidth
              label="Option Name"
              size="small"
              placeholder="Enter Option Name"
              error={!!errors?.name}
              helperText={errors?.name?.message}
              disabled={readOnly}
              InputProps={{
                readOnly: option && readOnly,
              }}
            />
          </Grid>

          {!!groupOptions?.length && (
            <Grid item xs={12}>
              <Controller
                name="group"
                control={control}
                rules={{ required: "Please select a group." }}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel id="select-group">Group</InputLabel>

                    <Select
                      {...field}
                      fullWidth
                      labelId="select-group"
                      label="Group"
                      placeholder="Select Option Group"
                      size="small"
                      id="role"
                      value={field?.value ?? ""}
                      readOnly={option && readOnly}
                    >
                      {groupOptions.map((option, index) => (
                        <MenuItem key={`${option}-${index}`} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>

                    {!!errors.group && (
                      <FormHelperText error>
                        {errors.group.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
          )}

          {!!valueLabel && (
            <Grid item xs={12}>
              <TextField
                {...register(`value`, {
                  required: "Please provide a numeric value.",
                  validateAsNumber: true,
                })}
                fullWidth
                type={valueType === "Number" ? "number" : "text"}
                size="small"
                inputProps={
                  valueType === "Number"
                    ? {
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        step: 5,
                      }
                    : undefined
                }
                label={valueLabel}
                placeholder={valueLabel}
                error={!!errors?.value}
                helperText={errors?.value?.message}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Please select a status" }}
              render={({ field, value }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="select-status">Status</InputLabel>

                  <Select
                    fullWidth
                    labelId="select-status"
                    label="status "
                    placeholder="Select status"
                    value={value}
                    {...field}
                  >
                    {["Active", "Inactive"]?.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>

                  {!!errors?.scope && (
                    <FormHelperText>
                      {errors.scope && errors.scope.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </InnerShadowBox>

      <Box
        paddingX={3}
        paddingY={2}
        gap={2}
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button disabled={isBusy} onClick={onClose}>
          Cancel
        </Button>

        <Button
          disabled={isBusy}
          variant="outlined"
          onClick={handleSubmit(onSubmit)}
        >
          {option ? "Update" : "Add"}
        </Button>
      </Box>
    </LoadingWrapper>
  );
};

export default ListOptionForm;
